import { useEffect, useRef, useState, useCallback } from 'react';

const POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

/**
 * Hook that polls /version.json to detect new deployments.
 *
 * On first load it stores the current build hash. On subsequent polls,
 * if the hash has changed it sets `updateAvailable` to true.
 *
 * Returns:
 *  - updateAvailable: boolean indicating a new version was detected
 *  - applyUpdate: function that reloads the page to load the new version
 */
const useVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const initialVersion = useRef(null);
  const intervalRef = useRef(null);

  const checkVersion = useCallback(async () => {
    try {
      // Cache-bust to avoid CDN / browser cache returning stale data
      const response = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (!response.ok) return;

      const data = await response.json();
      const remoteVersion = data.version;

      if (!remoteVersion) return;

      if (initialVersion.current === null) {
        // First check – store the version the app was loaded with
        initialVersion.current = remoteVersion;
        return;
      }

      if (remoteVersion !== initialVersion.current) {
        setUpdateAvailable(true);
        // Stop polling once we know there's an update
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err) {
      // Network errors are expected when offline – silently ignore
      console.debug('[useVersionCheck] Could not fetch version.json:', err.message);
    }
  }, []);

  const applyUpdate = useCallback(() => {
    globalThis.location.reload();
  }, []);

  useEffect(() => {
    // Initial check shortly after mount (give the app time to settle)
    const timeout = setTimeout(checkVersion, 5000);

    // Periodic polling
    intervalRef.current = setInterval(checkVersion, POLL_INTERVAL_MS);

    // Also check when the tab regains focus (user comes back to the app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);

  return { updateAvailable, applyUpdate };
};

export default useVersionCheck;
